import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { createPortal } from 'react-dom';
import { useCruises, Cruise, TravelDetail } from '../context/CruiseContext';
import { useCruiseMetadata } from '../context/CruiseMetadataContext';
import { useFriends } from '../context/FriendContext';
import TravelingCompanionsSection from '../components/TravelingCompanionsSection';
import AddFriendToCruiseModal from '../components/AddFriendToCruiseModal';
import ShareCruiseModal from '../components/ShareCruiseModal';
import { CurrencyIcon } from '../components/CurrencyIcon';
import { 
  Calendar as CalendarIcon, 
  Calendar,
  MapPin, 
  Anchor, 
  Clock, 
  Edit, 
  Trash2, 
  ChevronLeft, 
  Loader2, 
  Info, 
  AlertCircle, 
  CheckCircle,
  Plane, 
  Train, 
  Car, 
  ArrowRight,
  FileText,
  Dice3 as Dice,
  TrendingUp,
  TrendingDown,
  ChefHat,
  UserCheck,
  Phone,
  Mail,
  CalendarPlus,
  Share2,
  X,
  Download,
  Wifi,
  GlassWater,
  ExternalLink,
  Star,
  Ship,
  Users,
  UserPlus,
  Home,
  UtensilsCrossed,
  Music,
  Navigation,
  Weight,
  Ruler
} from 'lucide-react';
import { parseISO, differenceInDays, isAfter, startOfDay } from 'date-fns';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import CustomCruiseIcon from '../components/CustomCruiseIcon';
import LazyImage from '../components/LazyImage';
import { CruiseDetailsSkeleton } from '../components/LoadingSkeleton';
import ShareCountdownImageFeed from '../components/ShareCountdownImageFeed';
import ShareCountdownImageStories from '../components/ShareCountdownImageStories';
import { shareCountdownImage } from '../utils/shareUtils';
import { calendarManager } from '../services/calendarService';
import { useDisplayPreferences } from '../hooks/useDisplayPreferences';
import { useCruiseItineraryTitle } from '../hooks/useCruiseItineraryTitle';
import { SHOW_FRIENDS_BUTTON } from '../config';
import { logEvent } from '../services/analyticsService';

const CruiseDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getCruise, deleteCruise, cruises, loading: cruisesLoading } = useCruises();
  const { getShipImageUrl, getManageUrl, getShipLocationUrl, getShipDeckplanUrl, getShipMetadata } = useCruiseMetadata();
  const { friends, shareCruiseWithFriend, unshareCruiseWithFriend, getCruiseSharedWith, getTravelingCompanions, inviteFriendToCruise } = useFriends();
  const { formatDate, formatDateTime: formatDateTimePrefs, formatCurrency, formatTonnage, formatDimensions, formatNumber, formatTimeString } = useDisplayPreferences();
  const [cruise, setCruise] = useState<Cruise | null>(null);
  const itineraryTitle = useCruiseItineraryTitle(cruise);
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  const [shipImageError, setShipImageError] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [travelingCompanions, setTravelingCompanions] = useState<any[]>([]);
  const [showShareFriendsModal, setShowShareFriendsModal] = useState(false);
  const [showAddFriendModal, setShowAddFriendModal] = useState(false);
  const [sharedWith, setSharedWith] = useState<string[]>([]);

  // Share with Friends button only when user has at least one restricted-access friend
  const hasRestrictedAccessFriend = friends.some((f) => f.accessLevel === 'restricted-access');
  const showShareFriendsButton = !!cruise && !!id && SHOW_FRIENDS_BUTTON && hasRestrictedAccessFriend;

  // Share countdown state
  const [isGeneratingShare, setIsGeneratingShare] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareFormat, setShareFormat] = useState<'feed' | 'stories'>('feed');
  const [imageGenerated, setImageGenerated] = useState(false);
  const shareImageRef = useRef<HTMLDivElement>(null);
  const hasLoadedInitialData = useRef(false);

  // Ensure skeleton shows for minimum duration
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinTimeElapsed(true);
    }, 500); // 500ms minimum skeleton display

    return () => clearTimeout(timer);
  }, []);

  const [companionsLoaded, setCompanionsLoaded] = useState(false);

  // Reset loading state only when navigating to a different cruise ID
  useEffect(() => {
    hasLoadedInitialData.current = false;
    setLoading(true);
    setDataLoaded(false);
    setCompanionsLoaded(false);
    setCruise(null);
  }, [id]);

  // Track when data is actually loaded (both cruise AND companions ready)
  useEffect(() => {
    if (!loading && cruise && companionsLoaded) {
      setDataLoaded(true);
      logEvent('view_cruise_details', { cruiseId: cruise.id, shipName: cruise.shipName });
    }
  }, [loading, cruise, companionsLoaded]);

  useEffect(() => {
    const fetchCruise = async () => {
      if (!id || isDeleting) return;
      
      try {
        // First, check if the cruise is already in the cruises array
        const existingCruise = cruises.find(c => c.id === id);
        
        if (existingCruise) {
          // Use the existing cruise data immediately without triggering full reload/skeleton
          setCruise(existingCruise);
          setLoading(false);
          hasLoadedInitialData.current = true;
          return;
        }

        // Only set full loading states if cruise has not finished initial load
        if (!hasLoadedInitialData.current) {
          setLoading(true);
          setDataLoaded(false);
          setCompanionsLoaded(false);
        }
        
        if (!cruisesLoading) {
          // Only fetch from database if cruises have finished loading and cruise wasn't found
          const cruiseData = await getCruise(id);
          setCruise(cruiseData);
          setLoading(false);
          hasLoadedInitialData.current = true;
        }
      } catch (error) {
        console.error('Error fetching cruise:', error);
        setLoading(false);
        setCompanionsLoaded(true);
      }
    };

    fetchCruise();
  }, [id, getCruise, cruises, cruisesLoading, isDeleting]);

  const cruiseShipName = cruise?.shipName;
  const cruiseDepartureDate = cruise?.departureDate;

  useEffect(() => {
    let isMounted = true;
    if (id && cruiseShipName && cruiseDepartureDate) {
      if (SHOW_FRIENDS_BUTTON && friends.length > 0) {
        getTravelingCompanions(id, cruiseShipName, cruiseDepartureDate)
          .then((res) => {
            if (isMounted) {
              setTravelingCompanions(res);
              setCompanionsLoaded(true);
            }
          })
          .catch((err) => {
            console.error('Error fetching traveling companions:', err);
            if (isMounted) setCompanionsLoaded(true);
          });
      } else {
        setCompanionsLoaded(true);
      }
    }
    return () => {
      isMounted = false;
    };
  }, [id, cruiseShipName, cruiseDepartureDate, friends, getTravelingCompanions]);

  useEffect(() => {
    let isMounted = true;
    if (id && SHOW_FRIENDS_BUTTON) {
      getCruiseSharedWith(id)
        .then((ids) => {
          if (isMounted) setSharedWith(ids);
        })
        .catch((err) => console.error('Error fetching sharedWith for cruise:', err));
    }
    return () => {
      isMounted = false;
    };
  }, [id, getCruiseSharedWith]);

  const handleDelete = async () => {
    if (!id) return;
    
    try {
      setIsDeleting(true);
      setDeleteLoading(true);
      setDeleteError(null);
      await deleteCruise(id);
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Error deleting cruise:', error);
      setIsDeleting(false); // Reset if delete fails
      if (error.message === 'permission-denied' || error.code === 'permission-denied') {
        setDeleteError('You do not have permission to delete this cruise.');
      } else {
        setDeleteError('An error occurred while deleting the cruise. Please try again.');
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleAddToCalendar = async () => {
    if (!cruise) return;
    
    try {
      setCalendarLoading(true);
      await calendarManager.addCruiseToCalendar(cruise);
    } catch (error) {
      console.error('Error adding cruise to calendar:', error);
      // You could add a toast notification here if you have one set up
    } finally {
      setCalendarLoading(false);
    }
  };

  const calculateTotalCost = (cruise: Cruise) => {
    // Only include cruise cost if it's NOT a casino comp
    const cruiseCost = cruise.isComplimentaryCasinoSailing ? 0 : Number(cruise.cruiseCost || 0);
    const portTaxesFees = Number(cruise.portTaxesFees || 0);
    const additionalCosts = Number(cruise.additionalCosts || 0);
    const onboardExpenses = Number(cruise.onboardExpenses || 0);
    
    return cruiseCost + portTaxesFees + additionalCosts + onboardExpenses;
  };

  const calculateGamblingNet = (cruise: Cruise) => {
    const wins = Number(cruise.gamblingWins || 0);
    const losses = Number(cruise.gamblingLosses || 0);
    return wins - losses;
  };

  const formatDateTimeLocal = (dateTimeStr: string) => {
    return formatDateTimePrefs(new Date(dateTimeStr));
  };

  const renderTravelDetails = (detail: TravelDetail) => {
    switch (detail.type) {
      case 'airplane':
        return (
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center mb-3">
              <Plane className="h-5 w-5 text-primary-600 mr-2" />
              <h4 className="font-medium text-blue-900">Flight Details</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-blue-800 font-medium">{detail.company}</p>
                <p className="text-sm text-primary-600">Flight {detail.flightNumber}</p>
              </div>
              <div>
                <p className="text-sm text-blue-800 font-medium">Reservation</p>
                <p className="text-sm text-primary-600">{detail.reservationNumber}</p>
              </div>
              <div className="md:col-span-2 flex items-center justify-between text-sm">
                <div>
                  <p className="text-blue-800 font-medium">{detail.departureLocation}</p>
                  <p className="text-primary-600">
                    {detail.departureTime && formatDateTimeLocal(detail.departureTime)}
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-primary-400 mx-2" />
                <div className="text-right">
                  <p className="text-blue-800 font-medium">{detail.arrivalLocation}</p>
                  <p className="text-primary-600">
                    {detail.arrivalTime && formatDateTimeLocal(detail.arrivalTime)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'train':
        return (
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="flex items-center mb-3">
              <Train className="h-5 w-5 text-purple-600 mr-2" />
              <h4 className="font-medium text-purple-900">Train Details</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-purple-800 font-medium">{detail.company}</p>
                <p className="text-sm text-purple-600">Train {detail.trainNumber}</p>
              </div>
              <div>
                <p className="text-sm text-purple-800 font-medium">Reservation</p>
                <p className="text-sm text-purple-600">{detail.reservationNumber}</p>
              </div>
              <div className="md:col-span-2 flex items-center justify-between text-sm">
                <div>
                  <p className="text-purple-800 font-medium">{detail.departureLocation}</p>
                  <p className="text-purple-600">
                    {detail.departureTime && formatDateTimeLocal(detail.departureTime)}
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-purple-400 mx-2" />
                <div className="text-right">
                  <p className="text-purple-800 font-medium">{detail.arrivalLocation}</p>
                  <p className="text-purple-600">
                    {detail.arrivalTime && formatDateTimeLocal(detail.arrivalTime)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'rental-car':
        return (
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center mb-3">
              <Car className="h-5 w-5 text-green-600 mr-2" />
              <h4 className="font-medium text-green-900">Rental Car Details</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-green-800 font-medium">{detail.company}</p>
                <p className="text-sm text-green-600">{detail.carType}</p>
              </div>
              <div>
                <p className="text-sm text-green-800 font-medium">Reservation</p>
                <p className="text-sm text-green-600">{detail.reservationNumber}</p>
              </div>
              <div>
                <p className="text-sm text-green-800 font-medium">Pickup</p>
                <p className="text-sm text-green-600">{detail.pickupLocation}</p>
                <p className="text-sm text-green-600">
                  {detail.pickupTime && formatDateTimeLocal(detail.pickupTime)}
                </p>
              </div>
              <div>
                <p className="text-sm text-green-800 font-medium">Return</p>
                <p className="text-sm text-green-600">{detail.returnLocation}</p>
                <p className="text-sm text-green-600">
                  {detail.returnTime && formatDateTimeLocal(detail.returnTime)}
                </p>
                </div>
            </div>
          </div>
        );
    }
  };

  // Show skeleton until both data is loaded AND minimum time has elapsed
  if (!dataLoaded || !minTimeElapsed) {
    return (
      <CruiseDetailsSkeleton />
    );
  }

  if (!cruise) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Cruise Not Found</h2>
        <p className="text-gray-600 mb-8">The cruise you're looking for doesn't exist or you don't have access to it.</p>
        <Link
          to="/dashboard"
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-500 transition duration-200 font-medium"
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          Back to Dashboard
        </Link>
      </div>
    );
  }

  const today = startOfDay(new Date());
  const departureDate = startOfDay(parseISO(cruise.departureDate));
  const returnDate = startOfDay(parseISO(cruise.returnDate));
  
  // Check if cruise return date has passed (including today)
  // This determines if post-cruise details should be available
  const isReturnDatePassed = returnDate <= today;
  const isFutureCruise = isAfter(departureDate, today);
  
  const daysUntilDeparture = isFutureCruise 
    ? differenceInDays(departureDate, today)
    : 0;
  const cruiseDuration = differenceInDays(returnDate, departureDate);
  const totalCost = calculateTotalCost(cruise);
  const gamblingNet = calculateGamblingNet(cruise);
  const hasPorts = cruise.ports && cruise.ports.length > 0;
  const ratings = cruise.ratings;
  
  // Get ship image URL from Firebase data
  const shipImageUrl = getShipImageUrl(cruise.cruiseLine, cruise.shipName);
  
  // Get ship metadata
  const shipMetadata = getShipMetadata(cruise.cruiseLine, cruise.shipName);

  // Determine edit button destination based on return date
  // Post-cruise details available when return date is today or has passed
  const editButtonDestination = isReturnDatePassed 
    ? `/post-cruise-details/${cruise.id}` 
    : `/edit-cruise/${cruise.id}`;

  // Handle back button navigation consistently with system back button
  const handleBackNavigation = () => {
    if (Capacitor.isNativePlatform()) {
      // In native app, always go to dashboard (consistent with BackButtonHandler)
      navigate('/dashboard', { replace: true });
    } else {
      // In web, use browser history
      navigate(-1);
    }
  };

  // Share countdown handlers
  const handleShareCountdown = () => {
    // Reset state and open the format selection modal
    setImageGenerated(false);
    setShowShareModal(true);
  };

  const handleGenerateAndShare = async () => {
    if (!cruise || !shareImageRef.current) return;

    try {
      setIsGeneratingShare(true);

      // Calculate days fresh at the time of sharing to ensure accuracy
      const now = new Date();
      const today = startOfDay(now);
      const daysUntilDeparture = differenceInDays(startOfDay(parseISO(cruise.departureDate)), today);
      
      await shareCountdownImage(shareImageRef, {
        shipName: cruise.shipName,
        cruiseLine: cruise.cruiseLine,
        daysUntilDeparture,
        format: shareFormat,
        itineraryTitle
      });
      
      if (Capacitor.isNativePlatform()) {
        // On native platforms, the share sheet opens directly, so dismiss the modal
        setShowShareModal(false);
        // No toast message needed - the native share sheet provides its own feedback
      } else {
        toast.success('Countdown image downloaded!');
        // Mark image as generated and keep modal open for sharing options
        setImageGenerated(true);
      }
    } catch (error) {
      console.error('Error generating share image:', error);
      
      // On native platforms, don't show error toast for user cancellation
      if (Capacitor.isNativePlatform()) {
        // Check if it's a user cancellation (common error messages)
        const errorMessage = error instanceof Error ? error.message.toLowerCase() : '';
        if (errorMessage.includes('cancelled') || errorMessage.includes('canceled') || errorMessage.includes('user')) {
          // User cancelled - no toast needed
          return;
        }
      }
      
      // Show error toast for actual errors or on web platform
      toast.error('Failed to share countdown image');
    } finally {
      setIsGeneratingShare(false);
    }
  };

  const handleNativeShare = async () => {
    if (!cruise) return;

    const shareText = `${daysUntilDeparture} days until my cruise on the ${cruise.shipName}! 🚢 Track your cruise adventures with Cruising Along.`;
    const shareUrl = 'https://keepcruisingalong.com';

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Cruise Countdown',
          text: shareText,
          url: shareUrl
        });
        setShowShareModal(false);
      } catch (error) {
        // User cancelled or error occurred
        console.log('Share cancelled or failed:', error);
      }
    }
  };

  const handleSocialShare = (platform: 'twitter' | 'facebook') => {
    if (!cruise) return;

    const shareText = `${daysUntilDeparture} days until my cruise on the ${cruise.shipName}! 🚢 Track your cruise adventures with Cruising Along.`;
    const shareUrl = 'https://keepcruisingalong.com';
    
    let url = '';
    
    if (platform === 'twitter') {
      url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    } else if (platform === 'facebook') {
      url = `https://www.facebook.com/sharer/sharer.php?quote=${encodeURIComponent(shareText)}&u=${encodeURIComponent(shareUrl)}`;
    }
    
    if (url) {
      window.open(url, '_blank', 'width=600,height=400');
      setShowShareModal(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: dataLoaded ? 1 : 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center">
          <button 
            onClick={handleBackNavigation}
            className="mr-4 p-2 rounded-full hover:bg-gray-200 transition-colors"
          >
            <ChevronLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">{cruise.shipName}</h1>
            <p className="text-gray-600">{cruise.cruiseLine}</p>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-3">
          <div className="flex gap-3">
            {showShareFriendsButton && (
              <button
                onClick={() => setShowShareFriendsModal(true)}
                className="flex items-center px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium"
                title="Share with friends"
              >
                <Users className="h-4 w-4 mr-2 shrink-0" />
                Share with Friends
              </button>
            )}
            {SHOW_FRIENDS_BUTTON && friends.length > 0 && (
              <button
                onClick={() => setShowAddFriendModal(true)}
                className="flex items-center px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium"
                title="Tag friend on cruise"
              >
                <UserPlus className="h-4 w-4 mr-2 shrink-0" />
                Add Friend on Cruise
              </button>
            )}
            <button
              onClick={handleAddToCalendar}
              disabled={calendarLoading}
              className="flex items-center px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {calendarLoading ? (
                <Loader2 className="h-4 w-4 mr-2 shrink-0 animate-spin" />
              ) : (
                <CalendarPlus className="h-4 w-4 mr-2 shrink-0" />
              )}
              {calendarLoading ? 'Adding...' : 'Add to Calendar'}
            </button>
          </div>
          <div className="flex gap-3">
            <Link
              to={editButtonDestination}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-500 transition duration-200 font-medium"
            >
              <Edit className="h-4 w-4 mr-2 shrink-0" />
              Edit
            </Link>
            <button
              onClick={() => setDeleteModal(true)}
              className="flex items-center px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition duration-200 font-medium"
            >
              <Trash2 className="h-4 w-4 mr-2 shrink-0" />
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Large layout grid: gap-8 provides appropriate spacing for main content + sidebar layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <motion.div 
            className="bg-white rounded-lg shadow-md overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {cruise.isComplimentaryCasinoSailing && (
              <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-4 py-1 flex items-center justify-center">
                <Dice className="h-4 w-4 mr-2" />
                <span className="text-sm font-medium">Casino Comp</span>
              </div>
            )}
            <div className="h-48 bg-gradient-to-r from-blue-900 to-blue-700 relative">
              {shipImageUrl && !shipImageError ? (
                <>
                  <LazyImage src={shipImageUrl} 
                    alt={`${cruise.shipName} - ${cruise.cruiseLine}`}
                    className="w-full h-full object-cover"
                    onError={() => {
                      // Mark image as failed so we can show fallback
                      setShipImageError(true);
                    }}
                  />
                  <div className="absolute bottom-0 left-0 right-0 h-2/3 bg-gradient-to-t from-black/60 to-transparent"></div>
                </>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <CustomCruiseIcon size={160} className="text-white/30" />
                </div>
              )}
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h2 className="text-2xl font-semibold">{cruise.shipName}</h2>
                    <p className="text-lg opacity-90">{cruise.cruiseLine}</p>
                    {itineraryTitle && (
                      <p className="text-sm font-medium text-teal-200 mt-0.5">{itineraryTitle}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 mt-2 sm:mt-0">
                    {isFutureCruise && (
                      <div className="flex items-center justify-center px-3 py-1 bg-teal-500 rounded-full text-white text-sm font-semibold">
                        {daysUntilDeparture === 0 ? 'Today!' : `${daysUntilDeparture} days until departure`}
                      </div>
                    )}
                    {cruise.completed && (
                      <div className="flex items-center justify-center px-3 py-1 bg-green-500 rounded-full text-white text-sm font-semibold">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Completed
                      </div>
                    )}
                    {cruise.bookingNumber && (
                      <div className="flex items-center justify-center px-3 py-1 bg-blue-500 rounded-full text-white text-sm font-semibold">
                        <FileText className="h-3 w-3 mr-1" />
                        Booking ID: {cruise.bookingNumber}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start space-x-3">
                  <CalendarIcon className="h-5 w-5 text-teal-500 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900">Departure</h3>
                    <p className="text-gray-700">
                      {formatDate(parseISO(cruise.departureDate), { includeWeekday: true, fullMonth: true })}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <CalendarIcon className="h-5 w-5 text-teal-500 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900">Return</h3>
                    <p className="text-gray-700">
                      {formatDate(parseISO(cruise.returnDate), { includeWeekday: true, fullMonth: true })}
                    </p>
                    <p className="text-sm text-gray-500">{cruiseDuration} days</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <MapPin className="h-5 w-5 text-teal-500 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900">Departure Port</h3>
                    <p className="text-gray-700">{cruise.departurePort}</p>
                  </div>
                </div>
                
                {(cruise.cabinType || cruise.cabinNumber) && (
                  <div className="flex items-start space-x-3">
                    <Anchor className="h-5 w-5 text-teal-500 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-gray-900">Cabin</h3>
                      {cruise.isGuaranteedCabin ? (
                        <div>
                          {cruise.cabinType && (
                            <p className="text-gray-700">{cruise.cabinType}</p>
                          )}
                          <span className="inline-block mt-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                            Guaranteed Cabin
                          </span>
                        </div>
                      ) : (
                        <p className="text-gray-700">
                          {cruise.cabinNumber && cruise.cabinType ? (
                            `${cruise.cabinNumber} (${cruise.cabinType})`
                          ) : cruise.cabinNumber ? (
                            cruise.cabinNumber
                          ) : cruise.cabinType ? (
                            cruise.cabinType
                          ) : null}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Ship Specifications Section */}
              {shipMetadata && (shipMetadata.yearBuilt || shipMetadata.lastRefurbishment || shipMetadata.grossTonnage || shipMetadata.length || shipMetadata.width || shipMetadata.decks || shipMetadata.passengers) && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                      <Ship className="h-5 w-5 text-teal-500 mr-2" />
                      Ship Specifications
                    </h3>
                    <Link
                      to={`/ship/${encodeURIComponent(cruise.cruiseLine)}/${encodeURIComponent(cruise.shipName)}`}
                      className="flex items-center px-3 py-1.5 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition-colors text-sm font-medium"
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      View Ship Details
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                    {shipMetadata.yearBuilt && (
                      <div className="flex items-start space-x-3">
                        <Calendar className="h-5 w-5 text-teal-500 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-gray-900">Year Built</h4>
                          <p className="text-gray-700">{shipMetadata.yearBuilt}</p>
                          {shipMetadata.lastRefurbishment && (
                            <p className="text-sm text-gray-600 mt-1">
                              Last Refurbished: {shipMetadata.lastRefurbishment}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    {shipMetadata.grossTonnage && (
                      <div className="flex items-start space-x-3">
                        <Weight className="h-5 w-5 text-teal-500 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-gray-900">Gross Tonnage</h4>
                          <p className="text-gray-700">{formatTonnage(shipMetadata.grossTonnage)}</p>
                        </div>
                      </div>
                    )}
                    {(shipMetadata.length || shipMetadata.width) && (
                      <div className="flex items-start space-x-3">
                        <Ruler className="h-5 w-5 text-teal-500 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-gray-900">Dimensions</h4>
                          <p className="text-gray-700">
                            {formatDimensions(shipMetadata.length, shipMetadata.width)}
                          </p>
                        </div>
                      </div>
                    )}
                    {shipMetadata.decks && (
                      <div className="flex items-start space-x-3">
                        <Home className="h-5 w-5 text-teal-500 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-gray-900">Decks</h4>
                          <p className="text-gray-700">{formatNumber(shipMetadata.decks)}</p>
                        </div>
                      </div>
                    )}
                    {shipMetadata.passengers && (
                      <div className="flex items-start space-x-3">
                        <Users className="h-5 w-5 text-teal-500 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-gray-900">Passenger Capacity</h4>
                          <p className="text-gray-700">{formatNumber(shipMetadata.passengers)}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {(() => {
                const manageUrl = getManageUrl(cruise.cruiseLine);
                const shipLocationUrl = getShipLocationUrl(cruise.cruiseLine, cruise.shipName);
                const shipDeckplanUrl = getShipDeckplanUrl(cruise.cruiseLine, cruise.shipName);
                const hasExternalActions = manageUrl || shipLocationUrl || shipDeckplanUrl;
                
                return hasExternalActions && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                      <ExternalLink className="h-5 w-5 text-teal-500 mr-2" />
                      External Actions
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      {manageUrl && (
                        <a 
                          href={manageUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-500 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-accent"
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          Manage Cruise
                        </a>
                      )}
                      {shipLocationUrl && (
                        <a 
                          href={shipLocationUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-500 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-accent"
                        >
                          <Navigation className="h-4 w-4 mr-2" />
                          Track Ship
                        </a>
                      )}
                      {shipDeckplanUrl && (
                        <a 
                          href={shipDeckplanUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-500 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-accent"
                        >
                          <MapPin className="h-4 w-4 mr-2" />
                          View Deck Plan
                        </a>
                      )}
                    </div>
                  </div>
                );
              })()}
              
              {/* Cruise Ratings Display */}
              {ratings && Object.values(ratings).some(rating => rating !== undefined && rating > 0) && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="font-medium text-gray-900 mb-4 flex items-center">
                    <Star className="h-5 w-5 text-yellow-500 mr-2" />
                    Cruise Ratings
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {ratings.shipExperience && ratings.shipExperience > 0 && (
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center">
                          <Ship className="h-4 w-4 text-gray-600 mr-2" />
                          <span className="text-sm text-gray-700">Ship Experience</span>
                        </div>
                        <div className="flex items-center">
                          <div className="flex space-x-0.5 mr-2">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                              <Star
                                key={rating}
                                className={`h-4 w-4 ${
                                  rating <= ratings.shipExperience!
                                    ? 'fill-accent-500 text-accent-500'
                                    : 'fill-gray-200 text-gray-200'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {ratings.shipExperience}/10
                          </span>
                        </div>
                      </div>
                    )}
                    {ratings.service && ratings.service > 0 && (
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center">
                          <Users className="h-4 w-4 text-gray-600 mr-2" />
                          <span className="text-sm text-gray-700">Service</span>
                        </div>
                        <div className="flex items-center">
                          <div className="flex space-x-0.5 mr-2">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                              <Star
                                key={rating}
                                className={`h-4 w-4 ${
                                  rating <= ratings.service!
                                    ? 'fill-accent-500 text-accent-500'
                                    : 'fill-gray-200 text-gray-200'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {ratings.service}/10
                          </span>
                        </div>
                      </div>
                    )}
                    {ratings.stateroom && ratings.stateroom > 0 && (
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center">
                          <Home className="h-4 w-4 text-gray-600 mr-2" />
                          <span className="text-sm text-gray-700">Stateroom</span>
                        </div>
                        <div className="flex items-center">
                          <div className="flex space-x-0.5 mr-2">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                              <Star
                                key={rating}
                                className={`h-4 w-4 ${
                                  rating <= ratings.stateroom!
                                    ? 'fill-accent-500 text-accent-500'
                                    : 'fill-gray-200 text-gray-200'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {ratings.stateroom}/10
                          </span>
                        </div>
                      </div>
                    )}
                    {ratings.foodDining && ratings.foodDining > 0 && (
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center">
                          <UtensilsCrossed className="h-4 w-4 text-gray-600 mr-2" />
                          <span className="text-sm text-gray-700">Food & Dining</span>
                        </div>
                        <div className="flex items-center">
                          <div className="flex space-x-0.5 mr-2">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                              <Star
                                key={rating}
                                className={`h-4 w-4 ${
                                  rating <= ratings.foodDining!
                                    ? 'fill-accent-500 text-accent-500'
                                    : 'fill-gray-200 text-gray-200'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {ratings.foodDining}/10
                          </span>
                        </div>
                      </div>
                    )}
                    {ratings.barBeverages && ratings.barBeverages > 0 && (
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center">
                          <GlassWater className="h-4 w-4 text-gray-600 mr-2" />
                          <span className="text-sm text-gray-700">Bar & Beverages</span>
                        </div>
                        <div className="flex items-center">
                          <div className="flex space-x-0.5 mr-2">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                              <Star
                                key={rating}
                                className={`h-4 w-4 ${
                                  rating <= ratings.barBeverages!
                                    ? 'fill-accent-500 text-accent-500'
                                    : 'fill-gray-200 text-gray-200'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {ratings.barBeverages}/10
                          </span>
                        </div>
                      </div>
                    )}
                    {ratings.entertainment && ratings.entertainment > 0 && (
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center">
                          <Music className="h-4 w-4 text-gray-600 mr-2" />
                          <span className="text-sm text-gray-700">Entertainment</span>
                        </div>
                        <div className="flex items-center">
                          <div className="flex space-x-0.5 mr-2">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                              <Star
                                key={rating}
                                className={`h-4 w-4 ${
                                  rating <= ratings.entertainment!
                                    ? 'fill-accent-500 text-accent-500'
                                    : 'fill-gray-200 text-gray-200'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {ratings.entertainment}/10
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {cruise.notes && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-start space-x-3">
                    <Info className="h-5 w-5 text-teal-500 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-gray-900">Notes</h3>
                      <p className="text-gray-700 whitespace-pre-line">{cruise.notes}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Travel Agent Information */}
          {(cruise.travelAgentName || cruise.travelAgentPhone || cruise.travelAgentEmail) && (
            <motion.div 
              className="bg-white rounded-lg shadow-md p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.15 }}
            >
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                <UserCheck className="h-5 w-5 text-teal-500 mr-2" />
                Travel Agent Information
              </h2>
              
              <div className="space-y-3">
                {cruise.travelAgentName && (
                  <div className="flex items-center space-x-3">
                    <UserCheck className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-600">Agent Name</p>
                      <p className="font-medium text-gray-900">{cruise.travelAgentName}</p>
                    </div>
                  </div>
                )}
                
                {cruise.travelAgentPhone && (
                  <div className="flex items-center space-x-3">
                    <Phone className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-600">Phone Number</p>
                      <a 
                        href={`tel:${cruise.travelAgentPhone}`}
                        className="font-medium text-primary-600 hover:text-primary-500 transition-colors duration-200"
                      >
                        {cruise.travelAgentPhone}
                      </a>
                    </div>
                  </div>
                )}
                
                {cruise.travelAgentEmail && (
                  <div className="flex items-center space-x-3">
                    <Mail className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-600">Email Address</p>
                      <a 
                        href={`mailto:${cruise.travelAgentEmail}`}
                        className="font-medium text-primary-600 hover:text-primary-500 transition-colors duration-200"
                      >
                        {cruise.travelAgentEmail}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {cruise.travelDetails && cruise.travelDetails.length > 0 && (
            <motion.div 
              className="bg-white rounded-lg shadow-md p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Travel Details</h2>
              <div className="space-y-4">
                {cruise.travelDetails.map((detail) => (
                  <div key={detail.id}>
                    {renderTravelDetails(detail)}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
          
          {cruise.ports && cruise.ports.length > 0 && (
            <motion.div 
              className="bg-white rounded-lg shadow-md p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                Itinerary {itineraryTitle && <span className="text-base font-normal text-gray-500">— {itineraryTitle}</span>}
              </h2>
              
              <div className="space-y-6">
                {cruise.ports.map((port, index) => (
                  <div key={port.id || index} className="relative pl-8 pb-6">
                    {index < cruise.ports.length - 1 && (
                      <div className="absolute top-6 bottom-0 left-[11px] w-0.5 bg-gray-200"></div>
                    )}
                    
                    <div className="absolute top-1 left-0 w-6 h-6 rounded-full bg-secondary-100 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-secondary-500"></div>
                    </div>
                    
                    <div>
                      <h3 className="font-semibold text-gray-900">{port.name || 'Port of Call'}</h3>
                      {port.date && (
                        <p className="text-gray-700">
                          {formatDate(parseISO(port.date), { includeWeekday: true, fullMonth: true })}
                        </p>
                      )}
                      
                      {(port.arrivalTime || port.departureTime) && (
                        <div className="flex items-center mt-1 text-sm text-gray-600">
                          <Clock className="h-4 w-4 mr-1" />
                          {port.arrivalTime && <span>Arrival: {formatTimeString(port.arrivalTime)}</span>}
                          {port.arrivalTime && port.departureTime && <span className="mx-2">|</span>}
                          {port.departureTime && <span>Departure: {formatTimeString(port.departureTime)}</span>}
                        </div>
                      )}
                      
                      {port.excursion && (
                        <div className="mt-2 p-2 bg-blue-50 border-l-4 border-blue-500 text-blue-800 text-sm rounded">
                          <span className="font-medium">Excursion:</span> {port.excursion}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
        
        <motion.div 
          className="space-y-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          {/* Traveling Companions Section - Top Right Sidebar */}
          {cruise && travelingCompanions.length > 0 && (
            <TravelingCompanionsSection companions={travelingCompanions} isPast={!isFutureCruise} />
          )}
          {isFutureCruise && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-semibold text-gray-800">Countdown</h2>
                <button
                  onClick={handleShareCountdown}
                  disabled={isGeneratingShare}
                  className="flex items-center px-3 py-1.5 bg-secondary-600 hover:bg-secondary-500 disabled:opacity-50 rounded-md transition-colors duration-200 text-sm font-medium text-white min-w-[80px] min-h-[32px] justify-center"
                >
                  {isGeneratingShare ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Share2 className="h-4 w-4 mr-1" />
                      Share
                    </>
                  )}
                </button>
              </div>
              
              <div className="bg-gradient-to-br from-primary-600 to-primary-800 text-white rounded-lg p-6 flex flex-col items-center">
                <p className="text-lg mb-2">Your cruise departs in</p>
                <div className="text-4xl font-bold mb-2">{daysUntilDeparture}</div>
                <p className="text-lg">days</p>
              </div>
              
              <div className="mt-4 flex justify-between text-center">
                <div>
                  <p className="text-xs text-gray-500">DEPARTURE</p>
                  <p className="font-medium text-gray-800">
                    {formatDate(parseISO(cruise.departureDate), { shortFormat: true })}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">DURATION</p>
                  <p className="font-medium text-gray-800">{cruiseDuration} days</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">RETURN</p>
                  <p className="font-medium text-gray-800">
                    {formatDate(parseISO(cruise.returnDate), { shortFormat: true })}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">Cruise Summary</h2>
            <ul className="space-y-3">
              <li className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Cruise Line</span>
                <span className="font-medium text-gray-900">{cruise.cruiseLine}</span>
              </li>
              <li className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Ship</span>
                <span className="font-medium text-gray-900">{cruise.shipName}</span>
              </li>
              <li className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Duration</span>
                <span className="font-medium text-gray-900">{cruiseDuration} days</span>
              </li>
              {cruise.cabinType && (
                <li className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Cabin Type</span>
                  <span className="font-medium text-gray-900">{cruise.cabinType}</span>
                </li>
              )}
              {(cruise.cabinNumber || cruise.isGuaranteedCabin) && (
                <li className={`flex items-center justify-between py-2 ${(cruise.bookingNumber || hasPorts || totalCost > 0) ? 'border-b border-gray-100' : ''}`}>
                  <span className="text-gray-600">Cabin</span>
                  <span className="font-medium text-gray-900">
                    {cruise.isGuaranteedCabin ? (
                      <span className="text-primary-600">Guaranteed</span>
                    ) : (
                      cruise.cabinNumber
                    )}
                  </span>
                </li>
              )}
              {cruise.bookingNumber && (
                <li className={`flex items-center justify-between py-2 ${(hasPorts || totalCost > 0) ? 'border-b border-gray-100' : ''}`}>
                  <span className="text-gray-600">Booking Number</span>
                  <span className="font-medium text-gray-900 flex items-center">
                    <FileText className="h-4 w-4 mr-1" />
                    ID: {cruise.bookingNumber}
                  </span>
                </li>
              )}
              {cruise.diningTime && (
                <li className={`flex items-center justify-between py-2 ${(cruise.hasDrinkPackage || cruise.hasInternetPackage || hasPorts || totalCost > 0) ? 'border-b border-gray-100' : ''}`}>
                  <span className="text-gray-600">Dining Time</span>
                  <span className="font-medium text-gray-900 flex items-center">
                    <ChefHat className="h-4 w-4 mr-1" />
                    {cruise.diningTime}
                  </span>
                </li>
              )}
              {(cruise.hasDrinkPackage || cruise.hasInternetPackage) && (
                <li className={`flex items-start justify-between py-2 ${(hasPorts || totalCost > 0) ? 'border-b border-gray-100' : ''}`}>
                  <span className="text-gray-600">Packages Booked</span>
                  <span className="font-medium text-gray-900 flex flex-col items-end">
                    {cruise.hasDrinkPackage && (
                      <span className="flex items-center">
                        <GlassWater className="h-4 w-4 mr-1" />
                        Drinks
                      </span>
                    )}
                    {cruise.hasInternetPackage && (
                      <span className={`flex items-center ${cruise.hasDrinkPackage ? 'mt-1' : ''}`}>
                        <Wifi className="h-4 w-4 mr-1" />
                        Internet
                      </span>
                    )}
                  </span>
                </li>
              )}
              {hasPorts && (
                <li className={`flex items-center justify-between py-2 ${totalCost > 0 ? 'border-b border-gray-100' : ''}`}>
                  <span className="text-gray-600">Ports of Call</span>
                  <span className="font-medium text-gray-900">{cruise.ports.length}</span>
                </li>
              )}
              {cruise.completed && (
                <li className={`flex items-center justify-between py-2 ${totalCost > 0 ? 'border-b border-gray-100' : ''}`}>
                  <span className="text-gray-600">Status</span>
                  <span className="font-medium text-green-600 flex items-center">
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Completed
                  </span>
                </li>
              )}
              {totalCost > 0 && (
                <li className="pt-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                    <CurrencyIcon className="h-5 w-5 text-gray-600 mr-1" />
                    Cost Summary
                  </h3>
                  <div className="space-y-2">
                    {!cruise.isComplimentaryCasinoSailing && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Cruise Cost</span>
                        <span className="font-medium text-gray-900">
                          {formatCurrency(cruise.cruiseCost)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Port Taxes & Fees</span>
                      <span className="font-medium text-gray-900">
                        {formatCurrency(cruise.portTaxesFees)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Additional Costs</span>
                      <span className="font-medium text-gray-900">
                        {formatCurrency(cruise.additionalCosts)}
                      </span>
                    </div>
                    {cruise.onboardExpenses && cruise.onboardExpenses > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Onboard Expenses</span>
                        <span className="font-medium text-gray-900">
                          {formatCurrency(cruise.onboardExpenses)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2 mt-2 border-t border-gray-100">
                      <span className="font-semibold text-gray-800">Total Cost</span>
                      <span className="font-bold text-gray-900">
                        {formatCurrency(totalCost)}
                      </span>
                    </div>
                  </div>
                </li>
              )}
            </ul>
          </div>

          {/* Gambling Summary for Casino Comps */}
          {cruise.completed && (cruise.gamblingWins || cruise.gamblingLosses) && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                <Dice className="h-5 w-5 text-yellow-500 mr-2" />
                Gambling Summary
              </h2>
              <div className="space-y-3">
                {cruise.gamblingWins && cruise.gamblingWins > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 flex items-center">
                      <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                      Wins
                    </span>
                    <span className="font-medium text-green-600">
                      {formatCurrency(cruise.gamblingWins)}
                    </span>
                  </div>
                )}
                {cruise.gamblingLosses && cruise.gamblingLosses > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 flex items-center">
                      <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
                      Losses
                    </span>
                    <span className="font-medium text-red-600">
                      {formatCurrency(cruise.gamblingLosses)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between pt-2 mt-2 border-t border-gray-100">
                  <span className="font-semibold text-gray-800">Net Result</span>
                  <span className={`font-bold ${gamblingNet >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {gamblingNet >= 0 ? '+' : ''}{formatCurrency(gamblingNet)}
                  </span>
                </div>
              </div>
            </div>
          )}
          
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <div className="flex items-start">
              <CheckCircle className="h-5 w-5 text-blue-500 mt-0.5 mr-3" />
              <div>
                <p className="text-blue-800 font-medium">Ready for your cruise?</p>
                <p className="text-primary-600 text-sm mt-1">
                  Make sure to check your cruise line's website for any updates or requirements before your departure.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
      
      {/* Delete Confirmation Modal - Using Portal */}
      {deleteModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">Delete Cruise</h2>
              <button
                onClick={() => {
                  setDeleteModal(false);
                  setDeleteError(null);
                }}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
            <p className="text-gray-600">
              Are you sure you want to delete this cruise? This action cannot be undone.
            </p>
            
            {deleteError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-800">{deleteError}</p>
              </div>
            )}
            
            <div className="flex gap-3 pt-4">
              <button
                onClick={() => {
                  setDeleteModal(false);
                  setDeleteError(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium"
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={deleteLoading}
              >
                {deleteLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <Trash2 className="h-5 w-5 mr-2" />
                )}
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Share Modal - Using Portal */}
      {showShareModal && cruise && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <motion.div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">Share Your Countdown</h2>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-6">
            
            {/* Format Selection */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Choose Format:</h4>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setShareFormat('feed')}
                  className={`p-3 rounded-lg border-2 transition-colors ${
                    shareFormat === 'feed'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <div className="text-sm font-medium">Feed Post</div>
                  <div className="text-xs text-gray-500 mt-1">1200×630px</div>
                  <div className="text-xs text-gray-500">Facebook, X (Twitter), MMS</div>
                </button>
                <button
                  onClick={() => setShareFormat('stories')}
                  className={`p-3 rounded-lg border-2 transition-colors ${
                    shareFormat === 'stories'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <div className="text-sm font-medium">Stories</div>
                  <div className="text-xs text-gray-500 mt-1">1080×1920px</div>
                  <div className="text-xs text-gray-500">Instagram, Facebook Stories</div>
                </button>
              </div>
            </div>
            
            {/* Generate Image Button */}
            <div>
              <button
                onClick={handleGenerateAndShare}
                disabled={isGeneratingShare}
                className="w-full flex items-center justify-center px-4 py-3 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {isGeneratingShare ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Generating Image...
                  </>
                ) : (
                  <>
                    {Capacitor.isNativePlatform() ? (
                      <>
                        <Share2 className="h-5 w-5 mr-2" />
                        Select Format & Share
                      </>
                    ) : (
                      <>
                        <Download className="h-5 w-5 mr-2" />
                        Generate & Download Image
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
            
            {/* Sharing Options - Only show after image is generated */}
            {imageGenerated && (
              <>
                <p className="text-gray-600 mb-6">
                  Your countdown image has been downloaded! Choose how you'd like to share your excitement:
                </p>
                
                <div className="space-y-3">
                  {/* Native Share (if available) */}
                  {typeof navigator.share === 'function' && (
                    <button
                      onClick={handleNativeShare}
                      className="w-full flex items-center justify-center px-4 py-3 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium"
                    >
                      <Share2 className="h-5 w-5 mr-2" />
                      Share via Device
                    </button>
                  )}
                  
                  {/* Social Media Options */}
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => handleSocialShare('twitter')}
                      className="flex items-center justify-center px-4 py-3 bg-black text-white rounded-md hover:bg-gray-800 transition duration-200"
                    >
                      <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"/>
                      </svg>
                      X (Twitter)
                    </button>
                    
                    <button
                      onClick={() => handleSocialShare('facebook')}
                      className="flex items-center justify-center px-4 py-3 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium"
                    >
                      <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                      Facebook
                    </button>
                  </div>
                  
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800 flex items-start">
                      <Info className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                      <span><strong>Tip:</strong> For Instagram, upload the downloaded image manually to your story or post</span>
                    </p>
                  </div>
                </div>
              </>
            )}
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Hidden Share Image Components */}
      {cruise && (
        <>
          {/* Feed Format Image */}
          <div style={{ 
            position: 'fixed', 
            top: '-9999px', 
            left: '-9999px', 
            width: '1200px', 
            height: '630px', 
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: -1,
            display: shareFormat === 'feed' ? 'block' : 'none'
          }}>
            <ShareCountdownImageFeed
              ref={shareFormat === 'feed' ? shareImageRef : null}
              cruise={cruise}
              daysUntilDeparture={daysUntilDeparture}
              shipImageUrl={getShipImageUrl(cruise.cruiseLine, cruise.shipName)}
              itineraryTitle={itineraryTitle}
            />
          </div>
          
          {/* Stories Format Image */}
          <div style={{ 
            position: 'fixed', 
            top: '-9999px', 
            left: '-9999px', 
            width: '1080px', 
            height: '1920px', 
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: -1,
            display: shareFormat === 'stories' ? 'block' : 'none'
          }}>
            <ShareCountdownImageStories
              ref={shareFormat === 'stories' ? shareImageRef : null}
              cruise={cruise}
              daysUntilDeparture={daysUntilDeparture}
              shipImageUrl={getShipImageUrl(cruise.cruiseLine, cruise.shipName)}
              itineraryTitle={itineraryTitle}
            />
          </div>
        </>
      )}

      {/* Share with Friends Modal */}
      {showShareFriendsButton && (
        <ShareCruiseModal
          isOpen={showShareFriendsModal}
          onClose={() => setShowShareFriendsModal(false)}
          cruiseId={id}
          friends={friends}
          sharedWith={sharedWith}
          onShare={async (cruiseId, friendId) => {
            await shareCruiseWithFriend(cruiseId, friendId);
            setSharedWith([...sharedWith, friendId]);
          }}
          onUnshare={async (cruiseId, friendId) => {
            await unshareCruiseWithFriend(cruiseId, friendId);
            setSharedWith(sharedWith.filter(id => id !== friendId));
          }}
        />
      )}

      {/* Add Friend to Cruise Modal */}
      {cruise && showAddFriendModal && (
        <AddFriendToCruiseModal
          isOpen={showAddFriendModal}
          onClose={() => setShowAddFriendModal(false)}
          cruise={cruise}
          friends={friends}
          existingCompanionIds={travelingCompanions.map((c) => c.friendId)}
          sharedWith={sharedWith}
          onShareCruise={async (friendId) => {
            await shareCruiseWithFriend(id!, friendId);
            setSharedWith((prev) => Array.from(new Set([...prev, friendId])));
          }}
          onInvite={async (friendId) => {
            await inviteFriendToCruise(friendId, cruise);
            setCruise((prev) =>
              prev
                ? {
                    ...prev,
                    invitedFriendIds: Array.from(
                      new Set([...(prev.invitedFriendIds || []), friendId])
                    ),
                  }
                : null
            );
          }}
        />
      )}
    </motion.div>
  );
};

export default CruiseDetails;